import { toaster } from "@/components/ui/toaster";
import {
  createPlanificacionProductiva,
  getPlanificacionProductivaById,
  getPlanificacionProductivaList,
  updatePlanificacionProductiva,
  type GetPlanificacionProductivaListProps,
  type PlanificacionProductivaDetail,
} from "@/services/product-planification.service";
import { getAccountData, getCuentasByBusqueda } from "@/services/accounts.service";
import { getUsuariosMobileByCuenta } from "@/services/users.service";
import { getAxiosErrorMessage } from "@/utils/axios-error-message";
import { handlePermissionError } from "@/utils/permission-check";
import { AxiosError } from "axios";
import { Dispatch, SetStateAction } from "react";
import {
  FieldValues,
  UseFieldArrayReplace,
  UseFormSetValue,
} from "react-hook-form";
import { NavigateFunction } from "react-router-dom";
import {
  CultivoFormItem,
  ProductPlanificationCreationForm,
  ProductPlanificationListResponse,
} from "./product-planification.schema";
import { EstadosPlanificacionProductiva } from "@/constants/enums/status/entity-status-ids";
import { formatCurrencyInputToNumber, parseTonInputToNumber } from "@/utils/query-string";
import { HADifferenceItem } from "../create/HADifferenceModal";
import { AppRoutes } from "@/constants/routes";

const mapDetailToForm = (detail: PlanificacionProductivaDetail) => {
  const cultivos: CultivoFormItem[] = (detail?.cultivos ?? []).map((c) => ({
    idGrano: c?.idGrano ? [String(c?.idGrano)] : [],
    hectareasAsembrar: String(c?.hectareasASembrar ?? 0).replaceAll(".", ","),
    fechaEstimadaSiembra: c?.fechaEstimadaSiembra,
    consumoPropio: String(c?.consumoPropio ?? 0).replaceAll(".", ","),
    consumoPropioPicado: String(c?.consumoPropioPicado ?? 0).replaceAll(".", ","),
    costoEstimado: c?.costoEstimado ?? 0,
    toneladasRinde: String(c?.toneladasRinde ?? 0).replaceAll(".", ","),
  }));  

  return {
    cuentaOrigen: detail?.cuentaOrigen,
    idUsuarioMobile: detail?.idUsuarioMobile,
    idCampana: String(detail?.idCampana ?? detail?.idCamp ?? ""),
    idZonaProductiva: detail?.idZonaProductiva
      ? [String(detail.idZonaProductiva)]
      : [],
    haPropias: String(detail?.hectareasPropias ?? 0).replaceAll(".", ","),
    haAlquiladas: String(detail?.hectareasAlquiladas ?? 0).replaceAll(".", ","),
    detalle: detail?.detalle ?? "",
    cultivos,
  };
};

interface FetchProductPlanificationHandlerProps {
  filters?: GetPlanificacionProductivaListProps;
  showLoad?: boolean;
  setLoading: Dispatch<SetStateAction<boolean>>;
  setList: Dispatch<SetStateAction<ProductPlanificationListResponse | null>>;
  navigate: NavigateFunction;
  fetchPermissions: () => Promise<void>;
}

export const getDefaultProductPlanificationFilters = (): GetPlanificacionProductivaListProps => ({
  Estados: [
    EstadosPlanificacionProductiva.Cargada,
    EstadosPlanificacionProductiva.DatosEnRevision,
  ],
  "P.Pagina": 1,
  "P.Records": 10,
});

export const fetchProductPlanificationListHandler = async ({
  filters,
  showLoad = true,
  setLoading,
  setList,
  navigate,
  fetchPermissions,
}: FetchProductPlanificationHandlerProps) => {
  try {
    if (showLoad) setLoading(true);

    const response = await getPlanificacionProductivaList({
      ...filters,
      "P.Pagina": filters?.["P.Pagina"] ?? 1,
      "P.Records": filters?.["P.Records"] ?? 10,
    });

    setList(response as any);
  } catch (error) {
    const handled = await handlePermissionError(error, navigate, fetchPermissions);
    if (!handled) {
      const errorObj = error as AxiosError;
      toaster.create({
        type: "error",
        title: getAxiosErrorMessage(
          errorObj,
          "No se pudieron obtener las planificaciones productivas.",
        ),
      });
    }
    throw error;
  } finally {
    if (showLoad) setLoading(false);
  }
};

interface SubmitFiltersHandlerProps {
  event: FieldValues;
  setPage: Dispatch<SetStateAction<number>>;
  fetchList: (filters?: GetPlanificacionProductivaListProps) => Promise<void>;
  setCurrentFilters: Dispatch<SetStateAction<GetPlanificacionProductivaListProps>>;
  setFiltersApplied: Dispatch<SetStateAction<boolean>>;
  setOpenFilters: Dispatch<SetStateAction<boolean>>;
  filtersEmpty: boolean;
}

const parseOptionalNumber = (val: any): number | undefined => {
  if (val === undefined || val === null) return undefined;
  if (typeof val === "object" && "value" in val) {
    return parseOptionalNumber((val as { value?: unknown }).value);
  }
  if (typeof val === "number") return Number.isNaN(val) ? undefined : val;
  if (typeof val === "string") {
    const t = val.trim();
    if (!t) return undefined;
    const n = Number(t);
    return Number.isNaN(n) ? undefined : n;
  }
  return undefined;
};

export const submitFiltersHandler = async ({
  event,
  setPage,
  fetchList,
  setCurrentFilters,
  setFiltersApplied,
  setOpenFilters,
  filtersEmpty,
}: SubmitFiltersHandlerProps) => {
  const estados = Array.isArray(event?.estados)
    ? (event.estados as unknown[]).reduce<number[]>((acc, item) => {
        if (Array.isArray(item)) {
          return acc.concat(item as number[]);
        }
        if (typeof item === "string") {
          const parsed = item
            .split("|")
            .map(Number)
            .filter((n) => Number.isFinite(n));
          return acc.concat(parsed);
        }
        if (typeof item === "number") {
          acc.push(item);
        }
        return acc;
      }, [])
    : undefined;

  const cultivos = Array.isArray(event?.cultivos)
    ? event.cultivos.map(Number).filter((v: any) => !Number.isNaN(v))
    : undefined;


  const nextFilters: GetPlanificacionProductivaListProps = {
    CuentaOrigen: parseOptionalNumber(event?.cuentaOrigen),
    HectareasPropias: parseOptionalNumber(event?.hectareasPropias),
    HectareasAlquiladas: parseOptionalNumber(event?.hectareasAlquiladas),
    ConDesvioHectareas: event?.conDesvioHectareas,
    Estados: estados?.length ? estados : undefined,
    Cultivos: cultivos?.length ? cultivos : undefined,
    "P.Pagina": 1,
    "P.Records": 10,
  };

  setPage(1);

  setCurrentFilters(nextFilters);
  setFiltersApplied(!filtersEmpty);
  setOpenFilters(false);

  await fetchList(nextFilters);
};

interface ClearFiltersHandlerProps {
  setValue: UseFormSetValue<FieldValues>;
  setFiltersApplied: Dispatch<SetStateAction<boolean>>;
  setOpenFilters: Dispatch<SetStateAction<boolean>>;
  setPage: Dispatch<SetStateAction<number>>;
  setCurrentFilters: Dispatch<SetStateAction<GetPlanificacionProductivaListProps>>;
  fetchList: (filters?: GetPlanificacionProductivaListProps) => Promise<void>;
}

export const clearFiltersHandler = async ({
  setValue,
  setFiltersApplied,
  setOpenFilters,
  setPage,
  setCurrentFilters,
  fetchList,
}: ClearFiltersHandlerProps) => {
  setValue("cuentaOrigen", null);
  setValue("cultivos", []);
  setValue("estados", []);
  setValue("conDesvioHectareas", []);
  setValue("hectareasPropias", "");
  setValue("hectareasAlquiladas", "");

  setFiltersApplied(false);
  setOpenFilters(false);
  setPage(1);

  const nextFilters: GetPlanificacionProductivaListProps = {
    "P.Pagina": 1,
    "P.Records": 10,
  };
  setCurrentFilters(nextFilters);

  await fetchList(nextFilters);
};

export interface CreateProductPlanificationHandlerProps {
  setLoadingCreate: Dispatch<SetStateAction<boolean>>
  values: FieldValues
 granosById: Record<number, any>
 motivosDesvio: HADifferenceItem[]
 navigate: NavigateFunction
  fetchPermissions: () => Promise<void>;
}



export const createProductPlanificationHandler = async ({ setLoadingCreate, navigate, values, granosById, motivosDesvio, fetchPermissions }: CreateProductPlanificationHandlerProps) => {
  try {
        setLoadingCreate(true);
  
        const haPropias = parseTonInputToNumber(values.haPropias) ?? 0;
        const haAlquiladas = parseTonInputToNumber(values.haAlquiladas) ?? 0;
  
        const cuentaOrigen = Number((values.account)?.value);
        const idCampana = Number((values.idCampana ?? [])?.[0]);
        const idZonaProductiva = Number((values.idZonaProductiva ?? [])?.[0]);
  
        const cultivosBody = (values.cultivos ?? []).map((c: CultivoFormItem) => ({
          idGrano: Number((c?.idGrano ?? [])?.[0]),
          hectareasAsembrar: parseTonInputToNumber(c?.hectareasAsembrar) ?? 0,
          fechaEstimadaSiembra: c?.fechaEstimadaSiembra,
          consumoPropio: parseTonInputToNumber(c?.consumoPropio) ?? 0,
          consumoPropioPicado: parseTonInputToNumber(c?.consumoPropioPicado) ?? 0,
          costoEstimado: formatCurrencyInputToNumber((c?.costoEstimado ?? 0) as any),
          toneladasRinde: parseTonInputToNumber(c?.toneladasRinde) ?? 0,
        }));
  
        const sumCultivosForDiff = cultivosBody.reduce((acc: number, c: any) => {
          const grano = granosById[c.idGrano];
          if (!grano?.sumaHA) return acc;
          return acc + (c.hectareasAsembrar ?? 0);
        }, 0);
  
        const diff = haPropias + haAlquiladas - sumCultivosForDiff;
        const diffAbs = Math.abs(diff);
  
        if (diff !== 0) {
          const sumMotivos = (motivosDesvio ?? []).reduce(
            (acc, m) => acc + (m?.valorHectareas ?? 0),
            0,
          );
  
          if (diffAbs <= 0 || Math.abs(sumMotivos - diffAbs) > 0.00001) {
            toaster.create({
              type: "error",
              title: "Debe completar los motivos de desvío de hectáreas.",
            });
            return;
          }
        }
  
        await createPlanificacionProductiva({
          cuentaOrigen,
          idCampana,
          idZonaProductiva,
          haPropias,
          haAlquiladas,
          detalle: values.detalle ?? "",
          ...((values as any)?.idUsuarioMobile?.value
            ? { idUsuarioMobile: Number((values as any).idUsuarioMobile.value) }
            : {}),
          motivosDesvio: diff === 0 ? [] : motivosDesvio,
          cultivos: cultivosBody,
        });
  
        toaster.create({
          type: "success",
          title: "Planificación productiva creada.",
        });
  
        navigate(AppRoutes.PRODUCT_PLANIFICATION);
      } catch (e) {
        const handled = await handlePermissionError(e, navigate, fetchPermissions);
        if (!handled) {
          const errorObj = e as AxiosError;
          toaster.create({
            type: "error",
            title: getAxiosErrorMessage(
              errorObj,
              "No se pudo crear la planificación productiva.",
            ),
          });
        }
        throw e;
      } finally {
        setLoadingCreate(false);
      }
}

export interface FetchPPDetailHandlerProps {
  id: number;
  setLoadingDetail: Dispatch<SetStateAction<boolean>>;
  setLoadingCuentaUsuarios: Dispatch<SetStateAction<boolean>>;
  setDetail: Dispatch<SetStateAction<PlanificacionProductivaDetail | null>>;
  setCuentaUsuarios: Dispatch<SetStateAction<any[]>>;
  setMotivosDesvio: Dispatch<SetStateAction<HADifferenceItem[]>>;
  setValue: UseFormSetValue<ProductPlanificationCreationForm>;
  replaceCultivos: UseFieldArrayReplace<ProductPlanificationCreationForm, "cultivos">;
  navigate: NavigateFunction;
  fetchPermissions: () => Promise<void>;
}

 type SelectedAccountOption = { label: string; value: string; zonaComercial?: string };

 const buildSelectedAccountOption = (
   foundCuenta: any,
   accountData: any,
 ): SelectedAccountOption | null => {
   if (foundCuenta) {
     return {
       label: `${foundCuenta.titular} - ${foundCuenta.nroCuenta} - ${foundCuenta.tipoCuenta}`,
       value: String(foundCuenta.nroCuenta),
       zonaComercial: foundCuenta.zonaComercial,
     };
   }

   if (accountData) {
     return {
       label: `${accountData.razonSocial} - ${accountData.cuenta} - ${accountData.tipoCuenta}`,
       value: String(accountData.cuenta),
       zonaComercial: accountData.zonaComercial,
     };
   }

   return null;
 };

 const populateCuentaUsuariosAndSelectedMobileUser = async ({
   selectedAccount,
   mapped,
   setValue,
   setLoadingCuentaUsuarios,
   setCuentaUsuarios,
 }: {
   selectedAccount: SelectedAccountOption;
   mapped: ReturnType<typeof mapDetailToForm>;
   setValue: UseFormSetValue<ProductPlanificationCreationForm>;
   setLoadingCuentaUsuarios: Dispatch<SetStateAction<boolean>>;
   setCuentaUsuarios: Dispatch<SetStateAction<any[]>>;
 }) => {
   setValue("account", selectedAccount as any);

   setLoadingCuentaUsuarios(true);
   const usuarios = await getUsuariosMobileByCuenta({
     cuenta: String(selectedAccount.value),
   });
   setCuentaUsuarios(usuarios ?? []);
   setLoadingCuentaUsuarios(false);

   if (!mapped.idUsuarioMobile) return;

   const found = (usuarios ?? []).find(
     (u: any) => Number(u?.id) === Number(mapped.idUsuarioMobile),
   );
   if (!found) return;

   setValue("idUsuarioMobile", {
     label: `${found?.nombre} ${found?.apellido}`,
     value: found?.id,
   } as any);
 };

export const fetchPPDetailHandler = async ({
  id,
  setLoadingDetail,
  setLoadingCuentaUsuarios,
  setDetail,
  setCuentaUsuarios,
  setMotivosDesvio,
  setValue,
  replaceCultivos,
  navigate,
  fetchPermissions,
}: FetchPPDetailHandlerProps) => {
  if (!Number.isFinite(id) || id <= 0) {
    toaster.create({
      type: "error",
      title: "Id inválido.",
    });
    navigate(AppRoutes.PRODUCT_PLANIFICATION);
    return;
  }

  try {
    setLoadingDetail(true);

    const d = await getPlanificacionProductivaById(id);
    if (!d) {
      toaster.create({
        type: "error",
        title: "No se encontró la planificación productiva.",
      });
      navigate(AppRoutes.PRODUCT_PLANIFICATION);
      return;
    }

    setDetail(d);

    const mapped = mapDetailToForm(d);

    const cuentaOrigenStr = String(mapped.cuentaOrigen ?? "");
    const cuentas = cuentaOrigenStr
      ? await getCuentasByBusqueda({ busqueda: cuentaOrigenStr })
      : null;

    const foundCuenta = (cuentas ?? []).find(
      (c) => String(c?.nroCuenta) === cuentaOrigenStr,
    );

    const accountData =
      !foundCuenta && mapped.cuentaOrigen
        ? await getAccountData({
            NroCuenta: Number(mapped.cuentaOrigen),
          })
        : null;

    const selectedAccount = buildSelectedAccountOption(foundCuenta, accountData);
    if (selectedAccount) {
      await populateCuentaUsuariosAndSelectedMobileUser({
        selectedAccount,
        mapped,
        setValue,
        setLoadingCuentaUsuarios,
        setCuentaUsuarios,
      });
    }

    setValue("idCampana", mapped.idCampana ? [mapped.idCampana] : ([] as any));
    setValue("idZonaProductiva", mapped.idZonaProductiva ?? ([] as any));
    setValue("haPropias", mapped.haPropias);
    setValue("haAlquiladas", mapped.haAlquiladas);
    setValue("detalle", mapped.detalle);
    replaceCultivos(mapped.cultivos.length ? mapped.cultivos : ([] as any));
    setMotivosDesvio((d?.motivosDesvio ?? []) as any);
  } catch (error) {
    const handled = await handlePermissionError(error, navigate, fetchPermissions);
    if (!handled) {
      const errorObj = error as AxiosError;
      toaster.create({
        type: "error",
        title: getAxiosErrorMessage(
          errorObj,
          "No se pudo obtener la planificación productiva.",
        ),
      });
    }
    throw error;
  } finally {
    setLoadingDetail(false);
    setLoadingCuentaUsuarios(false);
  }
};

export interface UpdateProductPlanificationHandlerProps {
  values: ProductPlanificationCreationForm;
  detail: PlanificacionProductivaDetail;
  showHADifferenceButton: boolean;
  motivosDesvio: HADifferenceItem[];
  setLoadingUpdate: Dispatch<SetStateAction<boolean>>;
  navigate: NavigateFunction;
  fetchPermissions: () => Promise<void>;
}

export const updateProductPlanificationHandler = async ({
  values,
  detail,
  showHADifferenceButton,
  motivosDesvio,
  setLoadingUpdate,
  navigate,
  fetchPermissions,
}: UpdateProductPlanificationHandlerProps) => {
  try {
    setLoadingUpdate(true);

    const idZonaProductiva = Number((values.idZonaProductiva ?? [])?.[0]);

    const cultivosBody = (values.cultivos ?? []).map((c, idx) => {
      const current = (detail.cultivos ?? [])[idx];
      return {
        id: Number(current?.id ?? 0),
        idGrano: Number(
          (c?.idGrano ?? [])?.[0] ??
            current?.idGrano ??
            current?.idCultivo ??
            0,
        ),
        hectareasASembrar: parseTonInputToNumber(c?.hectareasAsembrar) ?? 0,
        consumoPropio: parseTonInputToNumber(c?.consumoPropio) ?? 0,
        consumoPropioPicado: parseTonInputToNumber(c?.consumoPropioPicado) ?? 0,
        costoEstimado: formatCurrencyInputToNumber((c?.costoEstimado ?? 0) as any),
        toneladasRinde: parseTonInputToNumber(c?.toneladasRinde) ?? 0,
        ...(c?.fechaEstimadaSiembra
          ? { fechaEstimadaSiembra: c.fechaEstimadaSiembra }
          : {}),
      };
    });

    await updatePlanificacionProductiva(detail.id, {
      id: detail.id,
      idEstado: EstadosPlanificacionProductiva.Presentada,
      idZonaProductiva: idZonaProductiva || Number(detail?.idZonaProductiva ?? 0),
      detalle: values.detalle ?? "",
      motivosDesvio: showHADifferenceButton ? motivosDesvio : [],
      cultivos: cultivosBody,
    });

    toaster.create({
      type: "success",
      title: "Planificación presentada exitosamente.",
    });

    navigate(AppRoutes.PRODUCT_PLANIFICATION);
  } catch (error) {
    const handled = await handlePermissionError(error, navigate, fetchPermissions);
    if (!handled) {
      const errorObj = error as AxiosError;
      toaster.create({
        type: "error",
        title: getAxiosErrorMessage(
          errorObj,
          "No se pudo actualizar la planificación productiva.",
        ),
      });
    }
    throw error;
  } finally {
    setLoadingUpdate(false);
  }
};
